import { Helmet } from "react-helmet-async";

/**
 * Componente centralizado de SEO. Renderiza title + meta description + Open
 * Graph + Twitter Card + canonical por página. Evita repetir 15 líneas de
 * <meta> en cada `useEffect` — un único punto de verdad, tipografía consistente
 * y OG tags siempre presentes.
 *
 * `url` es opcional — si no viene se usa la URL actual (window.location.href).
 * `image` cae al logo del sitio por default (para que WhatsApp/Facebook siempre
 * muestren tarjeta con imagen aunque olvidemos pasar una específica).
 */
const PROD_ORIGIN = "https://hittcg.store";
// Imagen social por defecto: composición 1200x630 (proporción OG estándar) con
// el logo sobre fondo oscuro. Se ve nítida en WhatsApp, Facebook, iMessage,
// Slack y Telegram. Las páginas de carta sobreescriben con la imagen de la
// carta cuando es más informativa para el share (via prop `image`).
const DEFAULT_IMAGE = `${PROD_ORIGIN}/og-image.png`;
const SITE_NAME = "HIT TCG";

const abs = (url) => {
  if (!url) return null;
  if (/^https?:\/\//.test(url)) return url;
  return `${PROD_ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
};

export function Seo({
  title,
  description,
  image,
  url,
  type = "website",
  keywords,
  noindex = false,
  jsonLd = null,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Marketplace de cartas TCG en Ecuador`;
  const desc = description || "Compra y vende cartas Pokémon y Riftbound en Ecuador en dólares con verificación de identidad, escrow y envíos a todo el país.";
  const imageUrl = abs(image) || DEFAULT_IMAGE;
  const canonical = url ? abs(url) : (typeof window !== "undefined" ? window.location.href.split("?")[0] : PROD_ORIGIN);
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={canonical} />
      {/* Open Graph — WhatsApp, Facebook, iMessage, Slack, Telegram */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      <meta property="og:locale" content="es_EC" />
      {/* Twitter Card — X, Discord, LinkedIn */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={imageUrl} />
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export default Seo;
